import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createCampaign, addRecipientsBulk } from '../services/api';

function CreateCampaign() {
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    from_email: '',
    html_body: '',
  });
  const [recipients, setRecipients] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.subject || !formData.from_email || !formData.html_body) {
      setError('All fields are required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Create campaign
      const campaign = await createCampaign(formData);
      console.log('Campaign created:', campaign);

      // Parse and add recipients if provided
      if (recipients.trim()) {
        const recipientList = parseRecipients(recipients);
        if (recipientList.length > 0) {
          await addRecipientsBulk(campaign.campaign_id, recipientList);
          console.log(`Added ${recipientList.length} recipients`);
        }
      }

      // Navigate to campaign details
      navigate(`/campaigns/${campaign.campaign_id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create campaign');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const parseRecipients = (text) => {
    // Parse CSV format: email,name (one per line)
    const lines = text.split('\n').filter(line => line.trim());
    const recipientList = [];

    for (const line of lines) {
      const parts = line.split(',').map(p => p.trim());
      if (parts[0]) {
        recipientList.push({
          email: parts[0],
          name: parts[1] || ''
        });
      }
    }

    return recipientList;
  };

  return (
    <div className="create-campaign">
      <h1>Create New Campaign</h1>

      {error && <div className="error">{error}</div>}

      <form onSubmit={handleSubmit} className="card">
        <div className="form-group">
          <label htmlFor="name">Campaign Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="e.g., Newsletter - January 2025"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="from_email">From Email *</label>
          <input
            type="email"
            id="from_email"
            name="from_email"
            value={formData.from_email}
            onChange={handleChange}
            placeholder="noreply@yourdomain.com"
            required
          />
          <small style={{ color: '#666' }}>Must be a verified SES email address</small>
        </div>

        <div className="form-group">
          <label htmlFor="subject">Email Subject *</label>
          <input
            type="text"
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            placeholder="Your Monthly Newsletter"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="html_body">Email Body (HTML) *</label>
          <textarea
            id="html_body"
            name="html_body"
            value={formData.html_body}
            onChange={handleChange}
            placeholder="<html>
<body>
  <h1>Hello {{name}}!</h1>
  <p>This is your email content...</p>
</body>
</html>"
            required
            style={{ fontFamily: 'monospace', minHeight: '300px' }}
          />
          <small style={{ color: '#666' }}>
            Use merge tags: {'{'}{'{'} name {'}'}{'}'}  {'{'}{'{'} email {'}'}{'}'}
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="recipients">Recipients (Optional - CSV format)</label>
          <textarea
            id="recipients"
            name="recipients"
            value={recipients}
            onChange={(e) => setRecipients(e.target.value)}
            placeholder="email@example.com,John Doe
another@example.com,Jane Smith"
            style={{ minHeight: '150px' }}
          />
          <small style={{ color: '#666' }}>
            Format: email,name (one per line). You can add recipients later.
          </small>
        </div>

        <div style={{ display: 'flex', gap: '1rem' }}>
          <button
            type="submit"
            className="button button-primary"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Campaign'}
          </button>
          <button
            type="button"
            className="button button-secondary"
            onClick={() => navigate('/campaigns')}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default CreateCampaign;
